import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, ArrowRight, Heart, Utensils, Globe, Shield, Camera, X, CheckCircle2 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import keralaImg from '../assets/kerala_luxury.webp';
import rajasthanImg from '../assets/rajasthan_luxury.webp';
import trainImg from '../assets/luxury_train_india.webp';
import wellnessImg from '../assets/ayurveda_wellness.webp';
import foodImg from '../assets/indian_food_luxury.webp';
import craftsImg from '../assets/indian_crafts.webp';
import birdsBg from '../assets/birds_itinerary_bg.webp';

const DiscoverIndia = () => {
  const [selectedPillar, setSelectedPillar] = useState(null);
  const navigate = useNavigate();

  const pillars = [
    {
      title: "Spiritual & Wellness",
      desc: "Authentic Ayurveda, Yoga, and meditation retreats in serene locales.",
      details: "Experience the profound wisdom of ancient Indian healing. Our spiritual journeys take you to the foothills of the Himalayas for Yoga and the lush forests of Kerala for authentic Ayurveda. We provide private consultations with traditional practitioners and secluded meditation spaces to help you reconnect with your inner self.",
      highlights: ["Personalized Ayurvedic Treatments", "Private Yoga Sessions", "Secluded Meditation Retreats", "Spiritual Himalayan Journeys"],
      img: wellnessImg,
      icon: Heart,
      tag: "Soulful Healing"
    },
    {
      title: "Diversity in One Country",
      desc: "From Himalayan peaks to tropical coasts, experience a world within one border.",
      details: "India is a continent masquerading as a country. From the snow-capped peaks of the Himalayas to the golden sands of the Thar Desert, and the emerald backwaters of the South to the vibrant jungles of Central India, we curate journeys that showcase this incredible geographic and cultural kaleidoscope in unparalleled luxury.",
      highlights: ["Himalayan Expeditions", "Coastal Escapes", "Desert Safaris", "Tropical Backwater Cruises"],
      img: keralaImg,
      icon: Globe,
      tag: "Infinite Variety"
    },
    {
      title: "Culinary Heritage",
      desc: "A symphony of spices and regional flavors crafted by master chefs.",
      details: "Embark on a gastronomic odyssey across the subcontinent. From the rich, saffron-infused flavors of Kashmiri Wazwan to the coastal delicacies of Goa and the vibrant street food of Mumbai, we offer exclusive kitchen access, spice market tours, and private dining experiences with India's most celebrated chefs.",
      highlights: ["Private Chef Tables", "Spice Market Tours", "Traditional Cooking Classes", "Regional Tasting Menus"],
      img: foodImg,
      icon: Utensils,
      tag: "Taste of India"
    },
    {
      title: "Medical & Wellness",
      desc: "World-class healthcare combined with premium post-treatment recovery.",
      details: "Combining state-of-the-art modern medicine with ancient healing traditions, India has become a global hub for medical tourism. We facilitate seamless end-to-end journeys, providing access to world-class hospitals and surgeons, followed by premium recovery in luxury wellness resorts that specialize in post-treatment care.",
      highlights: ["World-Class Medical Access", "Premium Recovery Resorts", "End-to-End Coordination", "Post-Treatment Wellness"],
      img: trainImg,
      icon: Shield,
      tag: "Care & Comfort"
    },
    {
      title: "Cultural Authenticity",
      desc: "Direct access to traditional crafts, ancient festivals, and local legends.",
      details: "Go beyond the surface to experience the true heart of India. We offer private access to ancient temples during sacred rituals, intimate meetings with master artisans keeping centuries-old crafts alive, and participation in vibrant local festivals that remain untouched by mass tourism.",
      highlights: ["Private Temple Rituals", "Artisan Workshops", "Festival Access", "Local Legend Storytelling"],
      img: craftsImg,
      icon: Camera,
      tag: "Pure Traditions"
    },
    {
      title: "Royal Hospitality",
      desc: "Stay in living palaces and experience the legendary 'Atithi Devo Bhava'.",
      details: "Experience hospitality as the Maharajas did. Our 'Royal Living' collection includes stays in meticulously restored palaces where the descendants of the royal families are often your hosts. From vintage car processions to royal gala dinners, we bring the era of Indian royalty to life for you.",
      highlights: ["Living Palace Stays", "Royal Gala Dinners", "Vintage Car Tours", "Heritage Suite Experiences"],
      img: rajasthanImg,
      icon: Sparkles,
      tag: "Regal Living"
    }
  ];

  return (
    <section className="py-24 bg-white overflow-hidden">
      <div className="container mx-auto px-6">
        <div className="text-center max-w-4xl mx-auto mb-20 space-y-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex items-center justify-center gap-3 text-orange-500 font-black text-xs uppercase tracking-[0.4em]"
          >
            <div className="w-10 h-[2px] bg-orange-500"></div>
            <span>Incredible India</span>
            <div className="w-10 h-[2px] bg-orange-500"></div>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-6xl font-serif font-bold text-slate-900 leading-tight"
          >
            Experience Bharath <br />
            <span className="text-orange-500 italic">Like Never Before</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-slate-600 text-lg font-medium max-w-2xl mx-auto"
          >
            We curate experiences that go beyond sightseeing, touching every aspect of India's rich and multifaceted identity. Click on any card to explore.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {pillars.map((pillar, idx) => (
            <motion.div
              key={pillar.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              onClick={() => setSelectedPillar(pillar)}
              className="group relative h-[300px] rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 cursor-pointer"
            >
              <img
                src={pillar.img}
                alt={pillar.title}
                className="w-full h-full object-cover transition-transform duration-[2s] group-hover:scale-110 brightness-[1.12] contrast-[1.03]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/10 to-transparent"></div>
              
              <div className="absolute top-8 left-8">
                <span className="bg-orange-500 text-white px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest shadow-lg">
                  {pillar.tag}
                </span>
              </div>

              <div className="absolute bottom-10 left-10 right-10 space-y-4">
                <div className="w-12 h-12 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center text-white border border-white/20 group-hover:bg-orange-500 group-hover:border-orange-500 transition-all duration-500">
                  <pillar.icon size={24} />
                </div>
                <div className="space-y-2">
                  <h3 className="text-2xl font-serif font-bold text-white tracking-wide">{pillar.title}</h3>
                  <p className="text-white/70 text-xs font-medium leading-relaxed line-clamp-2 group-hover:text-white transition-colors">
                    {pillar.desc}
                  </p>
                </div>
                <div className="pt-2">
                  <button className="inline-flex items-center gap-2 text-white font-bold uppercase tracking-widest text-[9px] border-b border-white/30 pb-1 hover:border-orange-500 hover:text-orange-500 transition-all">
                    Explore Details <ArrowRight size={12} />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-24 p-12 md:p-20 rounded-[4rem] border border-white/10 flex flex-col md:flex-row items-center justify-between gap-10 relative overflow-hidden group shadow-2xl"
        >
          {/* Birds Background Image */}
          <div className="absolute inset-0 z-0">
            <img 
              src={birdsBg} 
              alt="Vibrant Indian Birds" 
              className="w-full h-full object-cover transition-transform duration-[3s] group-hover:scale-105 brightness-[1.12] contrast-[1.03] blur-[4px]"
            />
            <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"></div>
          </div>

          <div className="space-y-4 text-center md:text-left relative z-10">
            <h3 className="text-3xl md:text-5xl font-serif font-bold text-white leading-tight">Bespoke Indian Itineraries</h3>
            <p className="text-white/80 font-medium max-w-xl text-lg">Our experts design personalized journeys that weave these core experiences into one unforgettable story.</p>
          </div>
          <Link to="/contact" className="relative z-10 bg-orange-500 text-white px-12 py-6 rounded-full font-black uppercase tracking-[0.2em] text-xs hover:bg-white hover:text-slate-900 transition-all shadow-2xl shadow-orange-500/20 whitespace-nowrap">
            Start Your Journey
          </Link>
        </motion.div>
      </div>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedPillar && (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 md:p-8">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedPillar(null)}
              className="absolute inset-0 bg-slate-900/90 backdrop-blur-xl"
            ></motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 40 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 40 }}
              className="relative bg-white w-full max-w-5xl rounded-[3rem] overflow-hidden shadow-2xl flex flex-col md:flex-row max-h-[90vh]"
            >
              <button
                onClick={() => setSelectedPillar(null)}
                className="absolute top-6 right-6 z-50 p-2 bg-slate-100 hover:bg-orange-500 hover:text-white rounded-full transition-all"
              >
                <X size={24} />
              </button>

              <div className="w-full md:w-1/2 h-64 md:h-auto relative">
                <img
                  src={selectedPillar.img}
                  alt={selectedPillar.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent md:bg-gradient-to-r"></div>
                <div className="absolute bottom-8 left-8 md:bottom-12 md:left-12">
                   <div className="flex items-center gap-3 text-orange-500 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full border border-white/20 mb-4 w-fit">
                    <selectedPillar.icon size={20} />
                    <span className="text-[10px] font-black uppercase tracking-[0.2em]">{selectedPillar.tag}</span>
                  </div>
                  <h2 className="text-4xl md:text-5xl font-serif font-bold text-white leading-tight">
                    {selectedPillar.title.split(' & ').map((part, i) => (
                      <React.Fragment key={i}>
                        {part}{i === 0 && selectedPillar.title.includes(' & ') && <><br /><span className="text-orange-400">&</span> </>}
                      </React.Fragment>
                    ))}
                  </h2>
                </div>
              </div>

              <div className="w-full md:w-1/2 p-8 md:p-16 overflow-y-auto custom-scrollbar">
                <div className="space-y-8">
                  <div className="space-y-4">
                    <h4 className="text-orange-600 font-black uppercase tracking-[0.3em] text-[10px]">The Experience</h4>
                    <p className="text-slate-700 text-lg leading-relaxed font-medium">
                      {selectedPillar.details}
                    </p>
                  </div>

                  <div className="space-y-4">
                    <h4 className="text-slate-400 font-black uppercase tracking-[0.3em] text-[10px]">Key Highlights</h4>
                    <div className="grid grid-cols-1 gap-3">
                      {selectedPillar.highlights.map((item, i) => (
                        <div key={i} className="flex items-center gap-3 p-4 bg-slate-50 rounded-2xl border border-slate-100 group hover:bg-orange-50 transition-colors">
                          <CheckCircle2 size={18} className="text-orange-500" />
                          <span className="text-slate-900 font-bold text-sm">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      setSelectedPillar(null);
                      navigate('/customised-destination');
                    }}
                    className="w-full bg-slate-900 text-white py-5 rounded-2xl font-bold uppercase tracking-[0.2em] text-xs hover:bg-orange-600 transition-all shadow-xl flex items-center justify-center gap-3 group"
                  >
                    Customize This Experience
                    <ArrowRight size={18} className="group-hover:translate-x-2 transition-transform" />
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default DiscoverIndia;
